import { Link as RouterLink } from 'react-router-dom';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { toPipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { formatEntityTagsLabel } from '@/features/shared/tags';
import { s } from '@/theme/tokens';
import { InfoField } from '@/ui/composites/InfoField';
import { formatDate } from '@/ui/formatters/formatDate';
import { LayoutGrid, LayoutGridItem } from '@/ui/layout/LayoutGrid';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Chip,
  formatStatusLabel,
  Link,
  resolveStatusChipTone,
  Surface,
} from '@/ui/primitives';
import type { StatusChipStatus } from '@/ui/primitives';
import type { BuildDto, UploadDto, UploadStatus } from '@flowqa/shared';
import type { ReactElement } from 'react';

export type UploadDetailsPanelsProps = {
  upload: UploadDto;
  build: BuildDto | null;
};

const mapUploadStatusToChipStatus = (status: UploadStatus): StatusChipStatus => {
  switch (status) {
    case 'processing':
      return 'processing';
    case 'completed_with_errors':
      return 'errored';
    case 'queued':
      return 'queued';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
  }
  const exhaustive: never = status;
  return exhaustive;
};

export const UploadDetailsPanels = ({ upload, build }: UploadDetailsPanelsProps): ReactElement => {
  const noDataLabel = 'No data';
  const noDataText = (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {noDataLabel}
    </Box>
  );
  const hasBuildPanel = Boolean(build?.id);
  const uploadedLabel = formatDate(upload.createdAt, { includeTime: true }) || noDataLabel;
  const uploadedByLabel = upload.createdByName;
  const resultsFileSlug = upload.resultsFileSlug ?? upload.resultsFileId ?? null;
  const resultsFileHref = resultsFileSlug ? `/results-files/${resultsFileSlug}` : null;
  const executionRunId = upload.latestExecutionRunId ?? null;
  const executionRunHref = executionRunId ? `/execution-runs/${executionRunId}` : null;
  const buildStatusLabel = build?.status ? formatStatusLabel(build.status) : noDataLabel;
  const pipelineLabel = build?.pipelineName ?? build?.pipelineId ?? noDataLabel;
  const pipelineSlug = build?.pipelineName ? toPipelineSlug(build.pipelineName) : null;
  const pipelineSegment =
    pipelineSlug && pipelineSlug.length > 0 ? pipelineSlug : (build?.pipelineId ?? null);
  const buildRunNumber = resolveBuildRunNumber(build?.buildKey);
  const pipelineHref = pipelineSegment ? `/pipelines/${pipelineSegment}` : '/pipelines';
  const buildHref =
    pipelineSegment && buildRunNumber !== null
      ? `/pipelines/${pipelineSegment}/runs/${buildRunNumber}`
      : pipelineHref;
  const buildLabel = build?.buildKey ?? build?.id ?? upload.buildId ?? noDataLabel;
  const projectLabel = upload.projectName;
  const sourceLabel = upload.source;
  const contentTypeLabel = upload.contentType;
  const tagsLabel = formatEntityTagsLabel(upload.tags);
  const uploadStatus = mapUploadStatusToChipStatus(upload.status);

  return (
    <Section paddingY="md">
      <Surface padding="lg" border>
        <LayoutGrid gap="md">
          <LayoutGridItem
            xs={12}
            sm={hasBuildPanel ? 3 : 4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingRight: s('md'), width: '100%' }}>
              <InfoField label="File Name" value={upload.fileName} gap="none" />
              <InfoField label="Content Type" value={contentTypeLabel} gap="none" />
              <InfoField label="Source" value={sourceLabel} gap="none" />
              <InfoField label="Tags" value={tagsLabel} gap="none" />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={hasBuildPanel ? 3 : 4}
            sx={{ borderRight: 0.5, borderColor: 'divider', display: 'flex' }}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField label="Project" value={projectLabel} gap="none" />
              <InfoField
                label="Results"
                value={
                  resultsFileHref && resultsFileSlug ? (
                    <Link
                      component={RouterLink}
                      to={resultsFileHref}
                      underline="hover"
                      color="inherit"
                    >
                      {resultsFileSlug}
                    </Link>
                  ) : (
                    noDataText
                  )
                }
                gap="none"
              />
              <InfoField
                label="Execution Run"
                value={
                  executionRunHref && executionRunId ? (
                    <Link
                      component={RouterLink}
                      to={executionRunHref}
                      underline="hover"
                      color="inherit"
                    >
                      {executionRunId}
                    </Link>
                  ) : (
                    noDataText
                  )
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
          <LayoutGridItem
            xs={12}
            sm={hasBuildPanel ? 3 : 4}
            sx={[
              { display: 'flex' },
              ...(hasBuildPanel ? [{ borderRight: 0.5, borderColor: 'divider' }] : []),
            ]}
          >
            <Stack gap="sm" sx={{ paddingX: s('md'), width: '100%' }}>
              <InfoField label="Uploaded" value={uploadedLabel} gap="none" />
              <InfoField label="Uploaded By" value={uploadedByLabel} gap="none" />
              <InfoField
                label="Status"
                value={
                  <Chip
                    label={formatStatusLabel(uploadStatus)}
                    tone={resolveStatusChipTone(uploadStatus)}
                    size="xs"
                    transform="uppercase"
                  />
                }
                gap="none"
              />
            </Stack>
          </LayoutGridItem>
          {hasBuildPanel ? (
            <LayoutGridItem xs={12} sm={3}>
              <Stack gap="sm" sx={{ paddingLeft: s('md') }}>
                <InfoField
                  label="Build"
                  value={
                    build?.id ? (
                      <Link component={RouterLink} to={buildHref} underline="hover" color="inherit">
                        {buildLabel}
                      </Link>
                    ) : (
                      buildLabel
                    )
                  }
                  gap="none"
                />
                <InfoField
                  label="Pipeline"
                  value={
                    pipelineSegment ? (
                      <Link
                        component={RouterLink}
                        to={pipelineHref}
                        underline="hover"
                        color="inherit"
                      >
                        {pipelineLabel}
                      </Link>
                    ) : (
                      pipelineLabel
                    )
                  }
                  gap="none"
                />
                <InfoField
                  label="Build Status"
                  value={
                    build?.status ? (
                      <Chip
                        label={buildStatusLabel}
                        tone={resolveStatusChipTone(build.status)}
                        size="xs"
                        transform="uppercase"
                      />
                    ) : (
                      noDataText
                    )
                  }
                  gap="none"
                />
              </Stack>
            </LayoutGridItem>
          ) : null}
        </LayoutGrid>
      </Surface>
    </Section>
  );
};
